import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Phone, Mail, Clock, Send, Loader2, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { MapImage } from "./MapImage";

const formSchema = z.object({
  nombreCompleto: z.string().min(3, { message: "Mínimo 3 caracteres" }),
  telefono: z.string()
    .min(10, { message: "Mínimo 10 dígitos" })
    .regex(/^\d{10}$/, { message: "Debe tener exactamente 10 dígitos" }),
  email: z.string().email({ message: "Correo inválido" }),
  tipoInteres: z.string().min(1, { message: "Seleccione una opción" }),
  mensaje: z.string().max(1000, { message: "Máximo 1000 caracteres" }).optional().or(z.literal("")),
  website: z.string().optional() // Honeypot
});

type FormValues = z.infer<typeof formSchema>;

const ContactForm = () => {
  const { toast } = useToast();
  
  // Verificar variable de entorno al montar el componente
  const webhookUrlEnv = import.meta.env.VITE_MAKE_WEBHOOK_URL;
  console.log("🔍 Variable de entorno al cargar componente:", webhookUrlEnv);
  
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nombreCompleto: "",
      telefono: "",
      email: "",
      tipoInteres: "",
      mensaje: "",
      website: "",
    },
  });

  // Location constants
  const address = "Calle 13 Nte, Santa Anita, 72094 Heroica Puebla de Zaragoza, Pue.";

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string>("");

  const onSubmit = async (values: FormValues) => {
    // Honeypot check
    if (values.website && values.website.trim() !== "") {
      console.log("Honeypot detectado, abortando envío");
      return;
    }

    try {
      setError("");
      setSuccess(false);
      setLoading(true);

      const webhookUrl = import.meta.env.VITE_MAKE_WEBHOOK_URL;
      
      console.log("Webhook URL desde env:", webhookUrl);
      console.log("Todas las variables env:", import.meta.env);
      
      if (!webhookUrl) {
        throw new Error("Error de configuración. Por favor, contacta al administrador.");
      }

      const payload = {
        fuente: "web_saman",
        nombreCompleto: values.nombreCompleto,
        telefono: values.telefono,
        email: values.email,
        tipoInteres: values.tipoInteres,
        mensaje: values.mensaje || "",
        urlPagina: window.location.href,
        timestamp: new Date().toISOString()
      };

      console.log("Enviando payload al webhook:", payload);

      const response = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Error del servidor: ${response.status}`);
      }

      toast({
        title: "¡Gracias!",
        description: "Te contactaremos en menos de 24 horas.",
      });
      setSuccess(true);
      reset();
    } catch (err: any) {
      console.error('Error en onSubmit:', err);
      const errorMessage = err?.message || "Error al enviar el formulario. Por favor, inténtalo de nuevo.";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  // WhatsApp Icon Component
  const WhatsAppIcon = ({ size = 20, className = "" }: { size?: number; className?: string }) => (
    <svg 
      viewBox="0 0 24 24" 
      width={size} 
      height={size} 
      fill="currentColor"
      className={className}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
  );

  const contactInfo = [
    {
      icon: WhatsAppIcon,
      title: "WhatsApp",
      value: "221 251 2363",
      action: "https://wa.me/5212212512363"
    },
    {
      icon: Mail,
      title: "Correo electrónico",
      value: "clientes@elsaman.com.mx",
      action: "mailto:clientes@elsaman.com.mx"
    },
    {
      icon: Phone,
      title: "Llamada telefónica",
      value: "Agendar horario personalizado",
      action: "tel:"
    },
    {
      icon: MapPin,
      title: "Ubicación",
      value: "Calle 13 Norte #1809, Col. El Tamborcito, Puebla, Puebla. C.P. 72094 (Frente a Sindicato SUTEM)",
      action: "https://www.google.com/maps/search/?api=1&query=Calle+13+Norte+%231809,+El+Tamborcito,+Puebla,+Pue+72094"
    }
  ];

  return (
    <section id="contacto" className="py-20 relative overflow-hidden">
      {/* background gradient mesh */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-24 -right-24 w-[520px] h-[520px] bg-[#C6A76D]/20 rounded-full blur-[120px]" />
        <div className="absolute -bottom-24 -left-24 w-[560px] h-[560px] bg-[#4B2E22]/15 rounded-full blur-[120px]" />
      </div>
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2A2520] mb-4">
            Contacto
          </h2>
          <p className="text-xl text-[#4B2E22] max-w-3xl mx-auto">
            Queremos estar cerca de ti en cada etapa de tu proyecto. En El Samán ofrecemos diferentes canales para brindarte atención personalizada, ya sea para resolver dudas, cotizar productos o agendar una asesoría.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
          }}
        >
          {/* Contact Form */}
          <motion.div 
            variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } }}
          >
            <Card className={cn(
              "shadow-xl border-0 bg-white/80 backdrop-blur-md transition-all duration-300",
              success && "border-2 border-green-300",
              error && "border-2 border-red-300"
            )}>
              <CardHeader>
                <CardTitle className="font-playfair text-2xl text-[#2A2520] flex items-center">
                  <Send className="mr-3 text-[#C6A76D]" size={24} />
                  Solicitar Asesoría Gratuita
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
                  {/* Honeypot field - hidden */}
                  <input
                    type="text"
                    name="website"
                    {...register("website")}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  {success && (
                    <div className="rounded-md bg-green-50 border border-green-200 text-green-800 px-4 py-2 text-sm">
                      ¡Gracias! Te contactaremos en menos de 24 horas.
                    </div>
                  )}
                  {error && (
                    <div className="rounded-md bg-red-50 border border-red-200 text-red-800 px-4 py-2 text-sm">
                      {error}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nombreCompleto">Nombre Completo *</Label>
                      <Input
                        id="nombreCompleto"
                        placeholder="Nombre completo"
                        {...register("nombreCompleto")}
                        className={cn(
                          "border-gray-300 focus:ring-2 focus:ring-[#C6A76D]/50 focus:border-[#C6A76D] transition-shadow",
                          errors.nombreCompleto && "border-red-500"
                        )}
                      />
                      {errors.nombreCompleto && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.nombreCompleto.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefono">Teléfono *</Label>
                      <Input
                        id="telefono"
                        type="tel"
                        placeholder="2210000000"
                        {...register("telefono")}
                        className={cn(
                          "border-gray-300 focus:ring-2 focus:ring-[#C6A76D]/50 focus:border-[#C6A76D] transition-shadow",
                          errors.telefono && "border-red-500"
                        )}
                      />
                      {errors.telefono && (
                        <p className="text-sm text-red-500">{errors.telefono.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Correo Electrónico *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="su@email.com"
                      {...register("email")}
                      className={cn(
                        "border-gray-300 focus:ring-2 focus:ring-[#C6A76D]/50 focus:border-[#C6A76D] transition-shadow",
                        errors.email && "border-red-500"
                      )}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-500">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tipoInteres">Tipo de Interés *</Label>
                    <Controller
                      control={control}
                      name="tipoInteres"
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger
                            className={cn(
                              "w-full border-gray-300 focus:ring-2 focus:ring-[#C6A76D]/50 focus:border-[#C6A76D]",
                              errors.tipoInteres && "border-red-500"
                            )}
                          >
                            <SelectValue placeholder="¿Qué te interesa?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Lambrín interior">
                              Lambrín interior
                            </SelectItem>
                            <SelectItem value="Lambrín exterior">
                              Lambrín exterior
                            </SelectItem>
                            <SelectItem value="Pisos Vinílicos">
                              Pisos Vinílicos
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.tipoInteres && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.tipoInteres.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mensaje">Mensaje</Label>
                    <Textarea
                      id="mensaje"
                      placeholder="Cuéntenos sobre su proyecto, medidas aproximadas, ubicación, etc."
                      rows={4}
                      {...register("mensaje")}
                      className={cn(
                        "border-gray-300 focus:ring-2 focus:ring-[#C6A76D]/50 focus:border-[#C6A76D] transition-shadow",
                        errors.mensaje && "border-red-500"
                      )}
                    />
                    {errors.mensaje && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.mensaje.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="hero"
                    size="lg"
                    className="w-full group"
                    disabled={isSubmitting || loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Enviando…
                      </>
                    ) : (
                      <>
                        Enviar Solicitud
                        <Send size={20} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <Badge variant="outline" className="text-xs">
                      <Clock size={12} className="mr-1" />
                      Respuesta en menos de 24 horas
                    </Badge>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Information */}
          <motion.div 
            className="space-y-8"
            variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } }}
          >
            <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-md">
              <CardHeader>
                <CardTitle className="font-playfair text-2xl text-[#2A2520]">
                  Información de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-center space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#C6A76D] to-[#B0975F] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <info.icon className="text-white" size={20} />
                    </div>
                    <div className="flex-grow">
                      <h4 className="font-semibold text-[#2A2520]">{info.title}</h4>
                      <a 
                        href={info.action}
                        className="text-[#4B2E22] hover:text-[#C6A76D] transition-colors"
                        target={info.action.startsWith('http') ? '_blank' : undefined}
                        rel={info.action.startsWith('http') ? 'noopener noreferrer' : undefined}
                      >
                        {info.value}
                      </a>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Business Hours */}
            <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-md">
              <CardHeader>
                <CardTitle className="font-playfair text-xl text-[#2A2520] flex items-center">
                  <Clock className="mr-3 text-[#C6A76D]" size={20} />
                  Horarios de Atención
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-[#4B2E22]">Lunes - Viernes</span>
                  <span className="font-medium">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#4B2E22]">Sábados</span>
                  <span className="font-medium">9:00 AM - 3:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#4B2E22]">Domingos</span>
                  <span className="font-medium">Cerrado</span>
                </div>
              </CardContent>
            </Card>

            {/* Map Embed */}
            <Card className="shadow-lg border-0 bg-white/80">
              <CardContent className="p-0">
                <p className="text-[#4B2E22] font-medium p-4 pb-2">Ubicación</p>
                <div className="px-4 pb-4">
                  <MapImage
                    address="Calle 13 Nte 1104, El Tamborcito, 72000 Heroica Puebla de Zaragoza, Pue."
                    staticImageUrl="/images/mapa-saman.png"
                    googleLink="https://www.google.com/maps/search/?api=1&query=Calle+13+Nte+1104,+El+Tamborcito,+72000+Heroica+Puebla+de+Zaragoza,+Pue."
                    height={360}
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;